import { fetchMergedCanonicalMatchRowsDesc } from "./canonicalMatches";

/** When |skillA − skillB| ≤ this value, order uses most recent H2H winner. */
export const RANKING_SKILL_TIE_BAND = 20;

export function matchTimestampMs(match) {
  if (!match || match.MatchDate == null) return 0;
  const d = match.MatchDate;
  if (typeof d.toDate === "function") return d.toDate().getTime();
  if (d.seconds != null) return d.seconds * 1000;
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function wrestlerDisplayName(data) {
  if (!data) return "";
  const full = (data.FullName && String(data.FullName).trim()) || "";
  if (full) return full;
  return `${(data.FirstName || "").trim()} ${(
    data.LastName || ""
  ).trim()}`.trim();
}

/**
 * @param {Array<{ id: string, data: object }>} rows Wrestlers in this ranking list only
 * @param {Map<string, object[]>} matchesByAthleteId athlete id -> match document data[]
 * @returns {Map<string, { ts: number, winnerId: string }>} key is "idSmaller\tidLarger"
 */
export function buildLatestH2HMap(rows, matchesByAthleteId) {
  const idSet = new Set(rows.map((r) => r.id));
  const latest = new Map();
  const pairKey = (x, y) => (x < y ? `${x}\t${y}` : `${y}\t${x}`);

  for (const row of rows) {
    const a = row.id;
    const matches = matchesByAthleteId.get(a) || [];
    for (const m of matches) {
      const opp = m.WrestlerID != null ? String(m.WrestlerID) : "";
      if (!opp || !idSet.has(opp)) continue;
      const ts = matchTimestampMs(m);
      if (ts <= 0) continue;
      let winner = null;
      if (m.Result === "Win") winner = a;
      else if (m.Result === "Loss") winner = opp;
      if (!winner) continue;
      const key = pairKey(a, opp);
      const prev = latest.get(key);
      if (!prev || ts > prev.ts) latest.set(key, { ts, winnerId: winner });
    }
  }
  return latest;
}

/**
 * Comparator for Array.sort: higher rank first (lower index = better).
 * @param {{ id: string, data: object }} a
 * @param {{ id: string, data: object }} b
 * @param {Map<string, { ts: number, winnerId: string }>} h2hMap
 */
export function compareWrestlersForRanking(a, b, h2hMap) {
  const sa = Number(a.data && a.data.Skill) || 0;
  const sb = Number(b.data && b.data.Skill) || 0;
  if (Math.abs(sa - sb) <= RANKING_SKILL_TIE_BAND) {
    const idA = a.id;
    const idB = b.id;
    const key = idA < idB ? `${idA}\t${idB}` : `${idB}\t${idA}`;
    const h = h2hMap.get(key);
    if (h && h.winnerId === idA) return -1;
    if (h && h.winnerId === idB) return 1;
  }
  if (sb !== sa) return sb - sa;
  return wrestlerDisplayName(a.data).localeCompare(wrestlerDisplayName(b.data));
}

/**
 * @param {import("firebase/firestore").Firestore} db
 * @param {string[]} athleteIds
 * @param {{ matchesPerAthleteLimit?: number | null }} options
 *   If `0`, no match reads (skill-only sort). If null/undefined, loads full match history per athlete.
 */
export async function fetchMatchDocsForAthletes(db, athleteIds, options = {}) {
  const unique = [...new Set(athleteIds.filter(Boolean))];
  if (options.matchesPerAthleteLimit === 0) {
    const map = new Map();
    unique.forEach((id) => map.set(id, []));
    return map;
  }
  const cap = options.matchesPerAthleteLimit;
  const perSide =
    cap != null && cap > 0 ? Math.min(cap, 5000) : 5000;
  const map = new Map();
  await Promise.all(
    unique.map(async (id) => {
      try {
        const rows = await fetchMergedCanonicalMatchRowsDesc(db, id, perSide);
        map.set(
          id,
          rows.map((r) => r.data)
        );
      } catch (_) {
        map.set(id, []);
      }
    })
  );
  return map;
}

/**
 * Apply skill sort with H2H tie-break for wrestlers in the same displayed list.
 * Only H2H where both athletes appear in `rows` is considered.
 * @param {{ matchesPerAthleteLimit?: number | null }} options Pass `0` for no match reads (faster; Skill + name only).
 */
export async function sortWrestlersByRankingRules(db, rows, options = {}) {
  if (!rows || rows.length <= 1) return rows || [];
  const matchesBy = await fetchMatchDocsForAthletes(
    db,
    rows.map((r) => r.id),
    options
  );
  const h2h = buildLatestH2HMap(rows, matchesBy);
  return [...rows].sort((a, b) => compareWrestlersForRanking(a, b, h2h));
}
